import React, { useCallback, useEffect, useMemo } from "react";
import { Box, Button, Grid } from "@mui/material";
import { get } from "lodash";
import MainCard from "components/MainCard";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import DeveloperAccountSettingView from "./ViewDeveloperAccountBody";
import {
  getApiAndSecretKeys,
  generateApiAndSecretKeys,
} from "store/reducers/developerAccountSettings";
import type { RootState, AppDispatch } from "store";

const ViewDeveloperAccountSetting = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { companyName } = useSelector(
    (state: RootState) => state.companyTabData,
    shallowEqual
  );
  const selectedCompany = useSelector(
    (state: RootState) => state.dashboardSearch.selectedCompany,
    shallowEqual
  );

  const { onBoardedCompanyData } = useSelector((state: RootState) => state.auth, shallowEqual);

  const selectedCompanyId = useMemo(
    () => get(selectedCompany, "_id", ""),
    [selectedCompany]
  );

  const { apiKey, secret, accessToken, refreshToken } = useSelector(
    (state: RootState) =>
      state.DeveloperAccountSetting || {
        apiKey: "",
        secret: "",
        accessToken: "",
        refreshToken: "",
        error: null,
        action: "",
      },
      shallowEqual
    );

  const finalCompanyId = selectedCompanyId;

  const hasKeys =
    !!apiKey && apiKey !== "false" && !!secret && secret !== "false" && !!accessToken && accessToken !== "false" && !!refreshToken && refreshToken !== "false";

  useEffect(() => {
    if (!finalCompanyId) {
      console.error("Selected Company ID is missing, redirecting to dashboard");
      navigate("/dashboard");
      return;
    }
    dispatch(getApiAndSecretKeys(finalCompanyId));
  }, [finalCompanyId, navigate, dispatch]);

  const handleGenerateAPIKey = useCallback(async () => {
    if (!finalCompanyId) {
      alert("Company ID is missing");
      return;
    }
    await dispatch(generateApiAndSecretKeys(finalCompanyId));
  }, [finalCompanyId, dispatch]);

  const actionButtons = useMemo(
    () => (
      <Grid container justifyContent="flex-end" spacing={2}>
        {onBoardedCompanyData?.webhook_enabled && (
          <Grid item>
            <Button
              variant="contained"
              onClick={() => {
                handleGenerateAPIKey();
              }}
              className="main-card-button"
            >
              {hasKeys
                ? "Update New API Key & Secret"
                : "Generate API Key & Secret"}
            </Button>
          </Grid>
        )}
      </Grid>
    ),
    [handleGenerateAPIKey, onBoardedCompanyData, hasKeys]
  );

  return (
    <MainCard title={companyName} secondary={actionButtons}>
      <Box sx={{ width: "100%", p: 2 }}>
        <DeveloperAccountSettingView
          selectedCompanyId={finalCompanyId}
          shouldDisplayListing={true}
          apiKey={apiKey}
          secret={secret}
          accessToken={accessToken}
          refreshToken={refreshToken}
        />
      </Box>
    </MainCard>
  );
};

export default React.memo(ViewDeveloperAccountSetting);
